import { cacheStats } from './cacheStats'
import {
  CACHE_PREFIX,
  CACHE_META_KEY,
  MAX_CACHE_SIZE,
  CACHE_EXPIRY_TIME,
  isWebView
} from './constants'
import { isDevelopment, logger } from './logger'

// 메모리 캐시를 fallback으로 사용
const memoryCache = new Map()

class CacheManager {
  constructor() {
    // 웹뷰 환경에서는 주기적으로 오래된 캐시 정리
    if (isWebView()) {
      this.startAutoCleanup()
    }
  }

  // 주기적인 캐시 정리 (웹뷰 환경용)
  startAutoCleanup() {
    setInterval(() => {
      try {
        const meta = this.getMeta()
        const now = Date.now()

        meta.keys.forEach((key) => {
          const item = localStorage.getItem(CACHE_PREFIX + key)
          if (item) {
            const { timestamp } = JSON.parse(item)
            if (now - timestamp > CACHE_EXPIRY_TIME) {
              this.delete(key)
            }
          }
        })
      } catch (error) {
        logger.warn('[LottieCache] 자동 정리 중 오류:', error)
      }
    }, CACHE_EXPIRY_TIME / 2) // 만료 시간의 절반마다 체크
  }

  // storage 사용 가능 여부 체크 (웹뷰 고려)
  isStorageAvailable() {
    try {
      // iOS 웹뷰에서 private 모드 체크
      if (isWebView() && /yanadoo_ios/.test(window.navigator.userAgent.toLowerCase())) {
        localStorage.setItem('__test__', '__test__')
        localStorage.removeItem('__test__')
      }

      const testKey = '__storage_test__'
      localStorage.setItem(testKey, testKey)
      localStorage.removeItem(testKey)
      return true
    } catch (e) {
      logger.warn('[LottieCache] Storage 사용 불가:', e)
      return false
    }
  }

  // 메타데이터 관리
  getMeta() {
    try {
      const meta = JSON.parse(localStorage.getItem(CACHE_META_KEY) || '{}')
      return {
        keys: meta.keys || [],
        timestamp: meta.timestamp || Date.now()
      }
    } catch (error) {
      logger.warn('[LottieCache] 메타데이터 로드 실패:', error)
      return { keys: [], timestamp: Date.now() }
    }
  }

  setMeta(meta) {
    try {
      localStorage.setItem(CACHE_META_KEY, JSON.stringify(meta))
    } catch (error) {
      logger.warn('[LottieCache] 메타데이터 저장 실패:', error)
    }
  }

  get(key) {
    const startTime = isDevelopment ? performance.now() : 0
    try {
      // 1. 먼저 메모리 캐시 확인
      const memoryData = memoryCache.get(key)
      if (memoryData) {
        cacheStats.recordHit('memory')
        isDevelopment && cacheStats.recordResponseTime(performance.now() - startTime)
        logger.debug(`[LottieCache] Memory Cache HIT - ${key}`)
        return memoryData
      }

      // 2. localStorage 사용 가능한 경우에만 시도
      if (this.isStorageAvailable()) {
        const cacheKey = CACHE_PREFIX + key
        const item = localStorage.getItem(cacheKey)

        if (!item) {
          cacheStats.recordMiss()
          logger.debug(`[LottieCache] Cache MISS - ${key}`)
          return null
        }

        const { data, timestamp } = JSON.parse(item)

        // 캐시 만료 체크
        if (Date.now() - timestamp > CACHE_EXPIRY_TIME) {
          cacheStats.recordMiss()
          logger.debug(`[LottieCache] Cache EXPIRED - ${key}`)
          this.delete(key)
          return null
        }

        // 유효한 데이터는 메모리 캐시에도 저장
        memoryCache.set(key, data)
        cacheStats.recordHit('storage')
        isDevelopment && cacheStats.recordResponseTime(performance.now() - startTime)
        logger.debug(`[LottieCache] Storage Cache HIT - ${key}`)
        return data
      }

      cacheStats.recordMiss()
      return null
    } catch (error) {
      cacheStats.recordError()
      logger.warn('[LottieCache] 캐시 로드 실패:', error)
      return null
    }
  }

  set(key, value) {
    const startTime = isDevelopment ? performance.now() : 0
    try {
      // 웹뷰에서는 데이터 크기 체크
      if (isWebView()) {
        const dataSize = new Blob([JSON.stringify(value)]).size
        if (dataSize > 1024 * 1024) {
          // 1MB 이상인 경우
          logger.warn(`[LottieCache] 큰 데이터 감지 (${dataSize} bytes) - 캐시 건너뛰기`)
          return
        }
      }

      // 1. 메모리 캐시에 저장
      if (memoryCache.size >= MAX_CACHE_SIZE) {
        const oldestKey = memoryCache.keys().next().value
        memoryCache.delete(oldestKey)
        logger.debug(`[LottieCache] Memory Cache EVICT - ${oldestKey}`)
      }
      memoryCache.set(key, value)
      logger.debug(`[LottieCache] Memory Cache SET - ${key}`)

      // 2. localStorage 사용 가능한 경우에만 시도
      if (this.isStorageAvailable()) {
        const meta = this.getMeta()
        const cacheKey = CACHE_PREFIX + key

        // 캐시 크기 제한 관리
        if (meta.keys.length >= MAX_CACHE_SIZE) {
          const oldestKey = meta.keys[0]
          this.delete(oldestKey)
          meta.keys = meta.keys.slice(1)
          logger.debug(`[LottieCache] Storage Cache EVICT - ${oldestKey}`)
        }

        try {
          // 새 데이터 저장 시도
          const cacheData = {
            data: value,
            timestamp: Date.now()
          }

          localStorage.setItem(cacheKey, JSON.stringify(cacheData))

          // 메타데이터 업데이트
          if (!meta.keys.includes(key)) {
            meta.keys.push(key)
            this.setMeta(meta)
          }

          isDevelopment &&
            logger.debug(
              `[LottieCache] Storage Cache SET - ${key}\n`,
              `데이터 크기: ${JSON.stringify(value).length} bytes`
            )
        } catch (storageError) {
          // 웹뷰에서 저장 실패 시 캐시 전체 초기화 후 재시도
          if (isWebView()) {
            logger.warn('[LottieCache] 웹뷰 저장 실패, 캐시 초기화 후 재시도')
            this.clear()
            try {
              localStorage.setItem(
                cacheKey,
                JSON.stringify({
                  data: value,
                  timestamp: Date.now()
                })
              )
              meta.keys = [key]
              this.setMeta(meta)
              return
            } catch (retryError) {
              logger.warn('[LottieCache] 웹뷰 재시도 실패, 캐시 사용 중단')
              return
            }
          }

          // 일반 브라우저에서는 기존 로직 유지
          cacheStats.recordStorageFailure()
          logger.warn('[LottieCache] Storage 저장 실패, 캐시 정리 후 재시도:', storageError)

          while (meta.keys.length > 0) {
            const oldestKey = meta.keys[0]
            this.delete(oldestKey)
            meta.keys = meta.keys.slice(1)

            try {
              localStorage.setItem(
                cacheKey,
                JSON.stringify({
                  data: value,
                  timestamp: Date.now()
                })
              )
              meta.keys.push(key)
              this.setMeta(meta)
              logger.debug(`[LottieCache] Storage Cache SET 재시도 성공 - ${key}`)
              break
            } catch (retryError) {
              logger.debug(`[LottieCache] Storage Cache SET 재시도 실패 - ${key}`)
              if (meta.keys.length === 0) break
            }
          }
        }
      }

      isDevelopment && cacheStats.recordResponseTime(performance.now() - startTime)
    } catch (error) {
      cacheStats.recordError()
      logger.warn('[LottieCache] 캐시 저장 실패:', error)
    }
  }

  delete(key) {
    try {
      // 1. 메모리 캐시에서 삭제
      memoryCache.delete(key)

      // 2. localStorage에서 삭제
      if (this.isStorageAvailable()) {
        const cacheKey = CACHE_PREFIX + key
        localStorage.removeItem(cacheKey)

        const meta = this.getMeta()
        meta.keys = meta.keys.filter((k) => k !== key)
        this.setMeta(meta)
      }

      logger.debug(`[LottieCache] Cache DELETE - ${key}`)
    } catch (error) {
      logger.warn('[LottieCache] 캐시 삭제 실패:', error)
    }
  }

  clear() {
    try {
      // 1. 메모리 캐시 초기화
      memoryCache.clear()

      // 2. localStorage 초기화
      if (this.isStorageAvailable()) {
        const meta = this.getMeta()
        meta.keys.forEach((key) => {
          localStorage.removeItem(CACHE_PREFIX + key)
        })
        localStorage.removeItem(CACHE_META_KEY)
      }

      // 3. 통계 초기화
      cacheStats.reset()
      logger.debug('[LottieCache] Cache CLEAR - 캐시 및 통계가 초기화되었습니다.')
    } catch (error) {
      cacheStats.recordError()
      logger.warn('[LottieCache] 캐시 초기화 실패:', error)
    }
  }
}

export const cacheManager = new CacheManager()
